/**
 * MapContinuity — reads AlvaAR's raw pose stream for the two things the vendored
 * API never says out loud: "I have no visual track" and "I started a new map".
 *
 * Pure bookkeeping on the 16 floats AlvaAR returns, so it is testable without
 * WASM. The backend decides what to do with the verdicts.
 *
 * ## "No visual track" — the frozen / zero translation signature
 *
 * Measured 2026-10-05 on ADVIO-15 by calling the vendored embind
 * `System.findCameraPose*` directly (the JS wrapper drops its status code):
 *
 *  - `findCameraPoseWithIMU` returns status 1 ("pose") on EVERY frame, 777/777,
 *    including the 17 frames before the map exists — where the translation is
 *    exactly (0,0,0) — and every frame after a loss, where the translation is
 *    bit-identical to the last real pose while the rotation keeps following the
 *    IMU quaternion. The backend used to call all of those 'tracking'.
 *  - `findCameraPose` returns null in both cases, as documented — but on
 *    scrambled input it, too, repeated one translation bit for bit (16 frames in
 *    3 shuffled runs).
 *
 * A real track never repeats a float32 3-vector exactly: 0 repeats and 0 zero
 * translations in 7,471 posed frames of the 10 normal-order vision runs. So
 * "exactly zero" and "exactly the previous one" are both treated as no track.
 *
 * ## "New map" — the unit-baseline signature
 *
 * After a loss AlvaAR does not relocalise; it throws its map away (vendor
 * status 2, then 3 = initialising) and builds a new one with a new origin, a new
 * orientation and a new, unrelated scale (3–7× different on this sequence). A
 * new monocular map normalises its first baseline to exactly one unit, so:
 *
 *  - vision path: the first pose after the null run has |t| = 1 (18/18 gaps in
 *    10 runs at FOV 68, 28/28 at the other FOVs; in 4 runs probed below the
 *    JS wrapper, every vendor status-2 "reset" ended in exactly such a pose and
 *    every such pose followed one);
 *  - IMU path: the new map continues from the frozen translation, and the first
 *    real pose is exactly one unit from it (|Δt| = 1.0000).
 *
 * Only the FIRST real pose after a no-track interval is examined, so a frame
 * that is legitimately near |t| = 1 mid-track cannot trigger it (30 such frames
 * within 1e-2 in the same runs, 0 within the 1e-4 tolerance used here — and
 * none of those followed a gap). A first pose that does not carry the
 * signature is reported as an ordinary pose, i.e. a same-map relocalisation,
 * which is what the backend always assumed — never observed on this build.
 */

/** A new AlvaAR map's first baseline, in its own units. */
export const MAP_INIT_BASELINE = 1;
/**
 * Tolerance on that baseline. Observed values are 1.00000 to float32 precision;
 * 1e-4 is three orders above that and far below anything motion produces by
 * chance on the single frame that is examined.
 */
export const MAP_INIT_TOLERANCE = 1e-4;

export type RawPoseVerdict =
  /** A real visual pose, continuing the current map. */
  | 'pose'
  /** AlvaAR returned a pose but has no visual track (zero or repeated translation). */
  | 'no-track'
  /** The first pose of a NEW AlvaAR map (its init, or a restart after a loss). */
  | 'new-map';

type T3 = [number, number, number];

export class MapContinuity {
  /** Raw translation of the previous result, or null when there was none. */
  private prevT: T3 | null = null;
  /** True until a real pose arrives after start, a null, or a no-track frame. */
  private inGap = true;
  /** Translation AlvaAR held through the current gap (null = the gap was nulls). */
  private gapRefT: T3 | null = null;

  /** AlvaAR returned null (initialising, or lost). */
  noResult(): void {
    this.prevT = null;
    this.inGap = true;
    this.gapRefT = null;
  }

  /** A call threw or returned something unusable: no evidence about the map. */
  skip(): void {
    this.prevT = null;
  }

  /** Judge one FINITE raw AlvaAR camera matrix (16 floats, column-major). */
  classify(mat: ArrayLike<number>): RawPoseVerdict {
    const t: T3 = [mat[12]!, mat[13]!, mat[14]!];
    const zero = t[0] === 0 && t[1] === 0 && t[2] === 0;
    const p = this.prevT;
    const frozen = p !== null && t[0] === p[0] && t[1] === p[1] && t[2] === p[2];
    this.prevT = t;
    if (zero || frozen) {
      this.inGap = true;
      this.gapRefT = t;
      return 'no-track';
    }
    if (!this.inGap) return 'pose';
    const ref = this.gapRefT;
    this.inGap = false;
    this.gapRefT = null;
    const unit = (x: number, y: number, z: number) =>
      Math.abs(Math.hypot(x, y, z) - MAP_INIT_BASELINE) < MAP_INIT_TOLERANCE;
    const fromOrigin = unit(t[0], t[1], t[2]);
    const fromHeld = ref !== null && unit(t[0] - ref[0], t[1] - ref[1], t[2] - ref[2]);
    return fromOrigin || fromHeld ? 'new-map' : 'pose';
  }

  /** Forget everything — a new AlvaAR instance or an explicit vendor reset. */
  reset(): void {
    this.noResult();
  }
}
